import { useQuery } from "@tanstack/react-query"
import { AlertCircle, ChevronLeft, ChevronRight, Download, TriangleAlert } from "lucide-react"
import { useState } from "react"

import { api, pedirBinario } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import type { DocumentoExportable } from "@/componentes/exportar/modelo"
import { aTasaMicro, comoTasa } from "@/componentes/tesoreria/conversion"
import { monto } from "@/componentes/tesoreria/dinero"
import { Boton } from "@/componentes/ui/Boton"
import { Insignia } from "@/componentes/ui/Insignia"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"
import { comoFecha } from "@/lib/fechas"

type Renglon = {
  operacion: number
  orden_id: number
  codigo_orden: string
  fecha_factura: string
  rif: string | null
  proveedor: string
  numero_factura: string
  numero_control: string | null
  tipo: string
  divisa: string
  tasa_cambio: string | null
  total: string
  exento: string
  base: string
  alicuota: string
  iva: string
  iva_retenido: string
  porcentaje_retencion: string
  comprobante_retencion: string | null
  avisos: string[]
}

type Pendiente = { orden_id: number; codigo_orden: string; proveedor: string; motivo: string }

type Libro = {
  anio: number
  mes: number
  renglones: Renglon[]
  totales: { total: string; exento: string; base: string; iva: string; iva_retenido: string }
  pendientes: Pendiente[]
}

const MESES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
]

/** La tasa como se lee: «848,5458», sin ceros de relleno. */
function tasaLegible(texto: string): string {
  const micro = aTasaMicro(texto)
  return micro === null ? texto : comoTasa(micro)
}

/** El mes anterior o el siguiente, cruzando el año cuando toca. */
function mover(periodo: { anio: number; mes: number }, paso: number) {
  const indice = periodo.anio * 12 + (periodo.mes - 1) + paso
  return { anio: Math.floor(indice / 12), mes: (indice % 12) + 1 }
}

/**
 * El libro como documento exportable (PDF con membrete, o Excel simple para trabajar).
 *
 * ⚠ **No sustituye al «Excel del SENIAT»**, que tiene el formato que pide el fisco y lo
 * arma el servidor. Esto lleva las mismas columnas que la pantalla más las que en ella
 * van en el segundo renglón (RIF, control, tasa, comprobante), cada una en la suya.
 */
function documentoLibro(libro: Libro, nombreMes: string): DocumentoExportable {
  const n = (t: string) => Number(t)
  const { totales } = libro
  return {
    titulo: "Libro de compras",
    subtitulo: `${nombreMes} · en bolívares a la tasa BCV del día de cada factura`,
    secciones: [
      {
        titulo: `Libro de compras de ${nombreMes}`,
        columnas: [
          { titulo: "N°", tipo: "numero" },
          { titulo: "Fecha", tipo: "fecha" },
          { titulo: "RIF", tipo: "texto" },
          { titulo: "Proveedor", tipo: "texto" },
          { titulo: "Orden", tipo: "texto" },
          { titulo: "Factura", tipo: "texto" },
          { titulo: "Control", tipo: "texto" },
          { titulo: "Moneda", tipo: "texto" },
          { titulo: "Tasa", tipo: "texto" },
          { titulo: "Total con IVA (Bs)", tipo: "monto" },
          { titulo: "Exento (Bs)", tipo: "monto" },
          { titulo: "Base (Bs)", tipo: "monto" },
          { titulo: "Alícuota %", tipo: "numero" },
          { titulo: "IVA (Bs)", tipo: "monto" },
          { titulo: "Retenido (Bs)", tipo: "monto" },
          { titulo: "% retención", tipo: "numero" },
          { titulo: "Comprobante", tipo: "texto" },
        ],
        filas: libro.renglones.map((r) => ({
          celdas: [
            r.operacion,
            r.fecha_factura.slice(0, 10),
            r.rif,
            r.proveedor,
            r.codigo_orden,
            r.numero_factura,
            r.numero_control,
            r.divisa,
            r.divisa !== "BS" && r.tasa_cambio ? tasaLegible(r.tasa_cambio) : null,
            n(r.total),
            n(r.exento),
            n(r.base),
            n(r.alicuota),
            n(r.iva),
            n(r.iva_retenido),
            n(r.porcentaje_retencion),
            r.comprobante_retencion,
          ],
        })),
        pie:
          libro.renglones.length > 0
            ? [
                [
                  "Totales",
                  null,
                  null,
                  null,
                  null,
                  null,
                  null,
                  null,
                  null,
                  n(totales.total),
                  n(totales.exento),
                  n(totales.base),
                  null,
                  n(totales.iva),
                  n(totales.iva_retenido),
                  null,
                  null,
                ],
              ]
            : undefined,
      },
      ...(libro.pendientes.length > 0
        ? [
            {
              titulo: "Órdenes que no entraron al libro",
              columnas: [
                { titulo: "Orden", tipo: "texto" as const },
                { titulo: "Proveedor", tipo: "texto" as const },
                { titulo: "Motivo", tipo: "texto" as const },
              ],
              filas: libro.pendientes.map((p) => ({
                celdas: [p.codigo_orden, p.proveedor, p.motivo],
              })),
            },
          ]
        : []),
    ],
  }
}

/**
 * El libro de compras del SENIAT, mes a mes.
 *
 * ⚠ **Aquí no se teclea nada: el libro se DERIVA de las órdenes de pago.** Un renglón
 * mal se corrige en su orden, y el libro sale corregido la próxima vez que se abre. Una
 * pantalla para editarlo sería un segundo sitio donde vive la misma factura.
 *
 * ⚠ **Las pendientes van ARRIBA, no al pie.** Son lo que falta para cerrar el mes, y
 * cada una es crédito fiscal que se pierde si el libro se entrega sin ella; al pie de
 * una tabla larga no las ve nadie.
 *
 * ⚠ **Los montos van en bolívares, ya convertidos por el servidor** con la tasa BCV del
 * día de cada factura. La pantalla no convierte: sería una segunda copia de la regla y
 * el Excel y la pantalla podrían decir cifras distintas.
 */
export function LibroCompras() {
  const hoy = new Date()
  const [periodo, setPeriodo] = useState({ anio: hoy.getFullYear(), mes: hoy.getMonth() + 1 })
  const [descargando, setDescargando] = useState(false)
  const [fallo, setFallo] = useState<string | null>(null)

  const consulta = `anio=${periodo.anio}&mes=${periodo.mes}`
  const libro = useQuery({
    queryKey: ["libro-compras", periodo.anio, periodo.mes],
    queryFn: () => api.get<Libro>(`/api/v1/libro-compras?${consulta}`),
  })

  const nombreMes = `${MESES[periodo.mes - 1]} de ${periodo.anio}`
  const datosLibro = libro.data

  async function descargar() {
    setDescargando(true)
    setFallo(null)
    try {
      const url = URL.createObjectURL(await pedirBinario(`/api/v1/libro-compras/excel?${consulta}`))
      const enlace = document.createElement("a")
      enlace.href = url
      enlace.download = `libro-compras-${periodo.anio}-${String(periodo.mes).padStart(2, "0")}.xlsx`
      enlace.click()
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (error) {
      setFallo(error instanceof Error ? error.message : "No se pudo descargar el libro.")
    } finally {
      setDescargando(false)
    }
  }

  return (
    <div>
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Libro de compras</h1>
          <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
            Las facturas recibidas en el mes, por su fecha de factura y en bolívares a la tasa BCV
            de ese día.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Boton
            variante="secundario"
            aria-label="Mes anterior"
            onClick={() => setPeriodo((p) => mover(p, -1))}
          >
            <ChevronLeft size={16} />
          </Boton>
          <span className="min-w-36 text-center font-medium capitalize" aria-live="polite">
            {nombreMes}
          </span>
          <Boton
            variante="secundario"
            aria-label="Mes siguiente"
            onClick={() => setPeriodo((p) => mover(p, 1))}
          >
            <ChevronRight size={16} />
          </Boton>
          {/* El Excel con el formato del SENIAT, que arma el servidor, y al lado el
              «Exportar» común: PDF con membrete o Excel simple para trabajar. */}
          <Boton variante="primario" onClick={descargar} disabled={descargando || !libro.data}>
            <Download size={16} /> {descargando ? "Preparando…" : "Excel del SENIAT"}
          </Boton>
          {datosLibro && (
            <Exportar
              titulo="Libro de compras"
              documento={() => documentoLibro(datosLibro, nombreMes)}
            />
          )}
        </div>
      </header>

      {fallo && (
        <p className="mb-3 text-sm" role="alert" style={{ color: "var(--destructive)" }}>
          {fallo}
        </p>
      )}

      {libro.isPending && <p className="py-6">Cargando…</p>}
      {libro.isError && (
        <p
          className="flex items-center gap-2 py-6"
          role="alert"
          style={{ color: "var(--destructive)" }}
        >
          <AlertCircle size={16} /> No se pudo armar el libro de {nombreMes}.
        </p>
      )}
      {libro.data && <Contenido libro={libro.data} nombreMes={nombreMes} />}
    </div>
  )
}

function Contenido({ libro, nombreMes }: { libro: Libro; nombreMes: string }) {
  const { renglones, totales, pendientes } = libro
  return (
    <>
      {pendientes.length > 0 && (
        <section className="tarjeta mb-4" aria-label="Órdenes que no entraron al libro">
          <h2 className="mb-2 flex items-center gap-2 font-medium">
            <TriangleAlert size={16} style={{ color: "var(--warning)" }} />
            {pendientes.length === 1
              ? "1 orden del periodo no entró al libro"
              : `${pendientes.length} órdenes del periodo no entraron al libro`}
          </h2>
          <p className="mb-2 text-sm" style={{ color: "var(--fg-muted)" }}>
            Resuélvelas antes de cerrar el mes: cada una es crédito fiscal que falta.
          </p>
          <ul className="space-y-1 text-sm">
            {pendientes.map((p) => (
              <li key={p.orden_id}>
                <span className="font-medium tabular-nums">{p.codigo_orden}</span> · {p.proveedor} —{" "}
                <span style={{ color: "var(--fg-muted)" }}>{p.motivo}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <p className="tarjeta mb-4 text-sm">
        <span className="tabular-nums">{renglones.length}</span>{" "}
        {renglones.length === 1 ? "factura" : "facturas"} · compras{" "}
        <span className="tabular-nums">{monto(totales.total)}</span> Bs · base{" "}
        <span className="tabular-nums">{monto(totales.base)}</span> · IVA{" "}
        <span className="tabular-nums">{monto(totales.iva)}</span> · retenido{" "}
        <span className="tabular-nums">{monto(totales.iva_retenido)}</span>
      </p>

      <Tabla etiqueta={`Libro de compras de ${nombreMes}`}>
        <Cabecera>
          <Th>N°</Th>
          <Th>Fecha</Th>
          <Th>Proveedor</Th>
          <Th>Factura / control</Th>
          <Th derecha>Total con IVA</Th>
          <Th derecha>Exento</Th>
          <Th derecha>Base</Th>
          <Th derecha>IVA</Th>
          <Th derecha>Retenido</Th>
        </Cabecera>
        <Cuerpo>
          {renglones.length === 0 && (
            <TablaVacia colSpan={9}>No hay facturas asentadas en {nombreMes}.</TablaVacia>
          )}
          {renglones.map((r) => (
            <Fila key={r.orden_id}>
              <Td numero>{r.operacion}</Td>
              <Td numero>{comoFecha(r.fecha_factura)}</Td>
              <Td>
                {r.proveedor}
                <div className="text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
                  {r.rif ?? "sin RIF"} · {r.codigo_orden}
                  {r.divisa !== "BS" && r.tasa_cambio && (
                    <>
                      {" "}
                      · {r.divisa} a {tasaLegible(r.tasa_cambio)}
                    </>
                  )}
                </div>
                {r.avisos.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {r.avisos.map((aviso) => (
                      <Insignia key={aviso} tono="aviso">
                        {aviso}
                      </Insignia>
                    ))}
                  </div>
                )}
              </Td>
              <Td numero>
                {r.numero_factura}
                <div className="text-xs" style={{ color: "var(--fg-muted)" }}>
                  {r.numero_control ?? "—"}
                </div>
              </Td>
              <Td derecha numero>
                {monto(r.total)}
              </Td>
              <Td derecha numero>
                {monto(r.exento)}
              </Td>
              <Td derecha numero>
                {monto(r.base)}
              </Td>
              <Td derecha numero>
                {monto(r.iva)}
              </Td>
              <Td derecha numero>
                {monto(r.iva_retenido)}
              </Td>
            </Fila>
          ))}
          {renglones.length > 0 && (
            <Fila>
              <Td colSpan={4} className="font-semibold">
                Totales
              </Td>
              <Td derecha numero className="font-semibold">
                {monto(totales.total)}
              </Td>
              <Td derecha numero className="font-semibold">
                {monto(totales.exento)}
              </Td>
              <Td derecha numero className="font-semibold">
                {monto(totales.base)}
              </Td>
              <Td derecha numero className="font-semibold">
                {monto(totales.iva)}
              </Td>
              <Td derecha numero className="font-semibold">
                {monto(totales.iva_retenido)}
              </Td>
            </Fila>
          )}
        </Cuerpo>
      </Tabla>
    </>
  )
}
